import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Film, ClipboardList, TrendingUp, Star, ArrowUpRight, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { EVALUATION_CATEGORIES, getCategoriesForPosition, getOverallScore } from '../data/criteria';
import { Badge } from '../components/ui/badge';
import PageHeader from '../components/PageHeader';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { PlayersAPI, EvaluationsAPI, StatsAPI } from '../api';

const StatCard = ({ icon: Icon, label, value, sub, accent = 'sky' }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="flex items-start justify-between mb-4">
      <div className={`h-10 w-10 rounded-lg bg-${accent}-500/10 border border-${accent}-500/20 flex items-center justify-center`}>
        <Icon className={`h-5 w-5 text-${accent}-400`} />
      </div>
      {sub && <span className="text-xs text-emerald-400 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {sub}</span>}
    </div>
    <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
    <div className="text-sm text-slate-500 mt-1">{label}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState({ players: 0, evaluations: 0, videos: 0, avgRating: 0, categoryAverages: {} });

  useEffect(() => {
    Promise.all([PlayersAPI.list(), EvaluationsAPI.list(), StatsAPI.team()])
      .then(([p, e, s]) => { setPlayers(p); setEvaluations(e); setStats(s); })
      .catch(err => console.error(err));
  }, []);

  const topPlayers = useMemo(() => {
    return players.map(p => {
      const evals = evaluations.filter(e => e.playerId === p.id);
      const latest = evals.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      const cats = getCategoriesForPosition(p.position);
      const score = latest ? parseFloat(getOverallScore(latest.ratings, cats)) : 0;
      return { ...p, score, evalCount: evals.length };
    }).sort((a, b) => b.score - a.score).slice(0, 5);
  }, [players, evaluations]);

  const recent = useMemo(() => (
    [...evaluations].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5)
      .map(e => {
        const player = players.find(p => p.id === e.playerId);
        const cats = getCategoriesForPosition(player?.position);
        return { ...e, player, score: getOverallScore(e.ratings, cats) };
      })
  ), [evaluations, players]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title={`Welcome, ${user?.name?.split(' ')[1] || user?.name || 'Coach'}.`}
        subtitle="Here's what's happening across your program today."
      />

      <OnboardingChecklist />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Active Players" value={stats.players} accent="sky" />
        <StatCard icon={ClipboardList} label="Evaluations" value={stats.evaluations} accent="emerald" />
        <StatCard icon={Film} label="Video Clips" value={stats.videos} accent="violet" />
        <StatCard icon={Star} label="Team Avg Rating" value={stats.avgRating || '0.0'} accent="amber" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display font-semibold text-lg">Top Rated Players</h3>
              <p className="text-sm text-slate-500">Based on latest evaluation</p>
            </div>
            <Link to="/app/players" className="text-sm text-sky-600 hover:text-sky-600 flex items-center gap-1">
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="space-y-2">
            {topPlayers.map((p, i) => (
              <Link key={p.id} to={`/app/players/${p.id}`}
                className="flex items-center gap-4 px-3 py-3 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="font-mono text-sm text-slate-400 w-5">{i + 1}</div>
                <img src={p.avatar} alt="" className="h-10 w-10 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{p.firstName} {p.lastName}</div>
                  <div className="text-xs text-slate-500">#{p.jerseyNumber}  {p.position}  {p.team}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-sky-600">{p.score ? p.score.toFixed(1) : ''}</div>
                  <div className="text-xs text-slate-500">{p.evalCount} evals</div>
                </div>
              </Link>
            ))}
            {topPlayers.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">Loading roster</div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Activity className="h-4 w-4 text-sky-600" />
            <h3 className="font-display font-semibold text-lg">Recent Activity</h3>
          </div>
          <div className="space-y-4">
            {recent.map(e => (
              <div key={e.id} className="pb-4 border-b border-slate-200 last:border-0 last:pb-0">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm text-slate-900 truncate">{e.player?.firstName} {e.player?.lastName}</div>
                  <Badge className="bg-sky-500/10 text-sky-600 border-sky-500/30 font-mono text-xs">{e.score}</Badge>
                </div>
                <div className="text-xs text-slate-500">{e.gameContext}</div>
                <div className="text-xs text-slate-400 mt-1">{new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}  {e.evaluatorName}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg mb-1">Team Category Averages</h3>
        <p className="text-sm text-slate-500 mb-5">Aggregate performance across the roster</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {EVALUATION_CATEGORIES.map(cat => {
            const avg = stats.categoryAverages?.[cat.id] || 0;
            const pct = (avg / 10) * 100;
            return (
              <div key={cat.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1.5 truncate">{cat.name}</div>
                <div className="font-mono font-bold text-lg text-slate-900 mb-2">{avg.toFixed ? avg.toFixed(1) : avg}</div>
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-sky-500 to-cyan-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
